import React from 'react';
import { CheckCheck, X, Info, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';

import type { NotificationItem } from '@/lib/api/notifications';
import {
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDismissNotification,
} from '@/hooks/useNotifications';
import { NotificationActionButton } from './NotificationActionButton';

interface NotificationInboxPanelProps {
  items: NotificationItem[];
  unreadCount: number;
  onClose: () => void;
}

const typeIcons = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle,
} as const;

const typeColors = {
  info: 'text-sky-500 dark:text-sky-400',
  warning: 'text-amber-500 dark:text-amber-400',
  error: 'text-red-500 dark:text-red-400',
  success: 'text-green-500 dark:text-green-400',
} as const;

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function NotificationInboxPanel({
  items,
  unreadCount,
  onClose,
}: NotificationInboxPanelProps) {
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const dismiss = useDismissNotification();

  const handleMarkRead = (id: string) => {
    markRead.mutate(id);
  };

  const handleDismiss = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    dismiss.mutate(id);
  };

  return (
    <div className="w-80 max-h-112 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 text-xs font-normal text-zinc-500 dark:text-zinc-400">
              {unreadCount} unread
            </span>
          )}
        </h3>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="flex items-center gap-1 text-xs text-sky-600 dark:text-sky-400
                       hover:text-sky-700 dark:hover:text-sky-300
                       disabled:opacity-50"
            title="Mark all as read"
          >
            <CheckCheck size={14} />
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
            No notifications
          </div>
        ) : (
          items.map((item) => {
            const Icon = typeIcons[item.type];
            const colorClass = typeColors[item.type];

            return (
              <div
                key={item.id}
                onClick={!item.isRead ? () => handleMarkRead(item.id) : undefined}
                onKeyDown={
                  !item.isRead
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleMarkRead(item.id);
                        }
                      }
                    : undefined
                }
                role={!item.isRead ? 'button' : undefined}
                tabIndex={!item.isRead ? 0 : undefined}
                aria-label={`${item.isRead ? 'Read' : 'Unread'}: ${item.title}`}
                className={`group relative px-4 py-3 border-b border-zinc-100 dark:border-zinc-700/50
                           hover:bg-zinc-50 dark:hover:bg-zinc-700/30 transition-colors
                           ${!item.isRead ? 'bg-sky-50 dark:bg-sky-900/20 border-l-2 border-l-sky-500 cursor-pointer' : ''}`}
              >
                {/* Dismiss button */}
                <button
                  onClick={(e) => handleDismiss(e, item.id)}
                  className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100
                             text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300
                             hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-all"
                  title="Dismiss"
                  aria-label={`Dismiss notification: ${item.title}`}
                >
                  <X size={14} />
                </button>

                <div className="flex gap-3 pr-6">
                  {/* Icon */}
                  <div className="shrink-0 mt-0.5">
                    <Icon size={16} className={colorClass} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {!item.isRead && (
                        <span className="shrink-0 w-2 h-2 bg-sky-500 rounded-full" />
                      )}
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                        {item.title}
                      </p>
                    </div>
                    <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400 line-clamp-2">
                      {item.message}
                    </p>
                    <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">
                      {timeAgo(item.createdAt)}
                    </p>

                    {/* Action buttons (dismiss type filtered — redundant with X) */}
                    {item.actions.filter((a) => a.type !== 'dismiss').length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.actions
                          .filter((a) => a.type !== 'dismiss')
                          .map((action, idx) => (
                            <NotificationActionButton
                              key={idx}
                              action={action}
                              notificationId={item.id}
                              onClose={onClose}
                            />
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
